import { useState } from "react";

import { faCalendar } from "@fortawesome/pro-regular-svg-icons";
import { faLayerGroup } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { BaseButton } from "shared/atoms/BaseButton";
import { ButtonsGroup } from "shared/atoms/ButtonsGroup";

import type { ActiveTab } from "./ActivityLibrarySidebar/ActivityLibrarySidebar";

export type ExercisesViewMode = "activities" | "weeks";

interface ProgramHeaderProps {
  activeTab: ActiveTab;
  activitiesLength: number;
  exercisesViewMode: ExercisesViewMode;
  onCollapseWeeks: () => void;
  onExpandWeeks: () => void;
  setExercisesViewMode: (viewMode: ExercisesViewMode) => void;
}

export const ProgramHeader: React.FC<ProgramHeaderProps> = ({
  activeTab,
  activitiesLength,
  exercisesViewMode,
  onCollapseWeeks,
  onExpandWeeks,
  setExercisesViewMode,
}) => {
  const { t } = useTranslation();
  const [weeksExpanded, setWeeksExpanded] = useState<boolean>(true);

  const handleToggleWeeks = () => {
    if (weeksExpanded) {
      onCollapseWeeks();
      setWeeksExpanded(false);
    } else {
      onExpandWeeks();
      setWeeksExpanded(true);
    }
  };

  return (
    <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", minHeight: 40 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        {activeTab === "exercises" && (
          <ButtonsGroup
            group={[
              {
                value: "activities",
                label: t("treatment.editor.view_mode.activities"),
                icon: <FontAwesomeIcon icon={faLayerGroup} />,
              },
              {
                value: "weeks",
                label: t("treatment.editor.view_mode.weeks"),
                icon: <FontAwesomeIcon icon={faCalendar} />,
              },
            ]}
            active={exercisesViewMode}
            setActive={value => setExercisesViewMode(value as ExercisesViewMode)}
          />
        )}

        {activeTab === "exercises" && exercisesViewMode === "weeks" && (
          <BaseButton
            variant="action"
            text={
              weeksExpanded
                ? t("treatment.editor.settings_sidebar.collapse_all")
                : t("treatment.editor.settings_sidebar.expand_all")
            }
            icon={weeksExpanded ? "chevron-up" : "chevron-down"}
            onClick={handleToggleWeeks}
          />
        )}
      </Stack>

      <Typography variant="subtitle1" sx={{ marginLeft: "auto" }}>
        {t("treatment.editor.activities_count", { activityType: activeTab, count: activitiesLength })}
      </Typography>
    </Stack>
  );
};
